"use strict";

/**
 * Shared accessible dialog helper (DGF-3989).
 *
 * Applies the W3C ARIA APG modal dialog pattern to a dialog element:
 * https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/
 *
 * Bootstrap 4's modal plugin traps focus but does not set `aria-modal` or make background content
 * inert, and Superdry's dialogs are largely hand-rolled and bypass the plugin entirely. This helper
 * gives any dialog - plugin-driven or not - the roles, focus handling and background inerting that
 * WCAG 4.1.2, 2.4.3 and 4.1.3 require.
 *
 * Usage:
 *     var accessibleDialog = require("../helpers/accessibleDialog");
 *
 *     accessibleDialog.open(document.getElementById("myDialog"), {
 *         initialFocus: ".close",
 *         onClose: function (dialog) { dialog.remove(); }
 *     });
 */

var FOCUSABLE_SELECTOR = [
    "a[href]",
    "area[href]",
    "button",
    "input",
    "select",
    "textarea",
    "iframe",
    "audio[controls]",
    "video[controls]",
    "[contenteditable]",
    "[tabindex]"
].join(",");

var ESCAPE_KEY = 27;
var TAB_KEY = 9;
var MAIN_LANDMARK_SELECTOR = "#maincontent";

// Presentational overlays that sit alongside a dialog rather than behind meaningful content. They
// are left out of the background inerting: they hold nothing for assistive technology to read, and
// an inert element is removed from hit-testing, which would stop the click-outside-to-close
// handlers that several dialogs bind to their backdrop from ever matching (DGF-3991).
var OVERLAY_SELECTOR = ".modal-backdrop,.modal-add-to-bag-backdrop,.modal-background";

var supportsInert = typeof HTMLElement !== "undefined" && "inert" in HTMLElement.prototype;
var idCounter = 0;

// Dialogs currently open, most recently opened last. Keeping a stack means a dialog opened on top
// of another restores the right background state and focus target when it closes.
var openDialogs = [];

// Per-modal state for the Bootstrap integration, which tracks its dialogs separately because
// Bootstrap owns their focus handling. See enhanceBootstrapModals.
var bootstrapRecords = new WeakMap();
var bootstrapEnhanced = false;

/**
 * Resolves a jQuery object, DOM node or selector string to a single DOM node.
 *
 * @param {jQuery|Element|string} target - the dialog reference supplied by the caller
 * @returns {Element|null} the resolved element, or null when it cannot be found
 */
function toElement(target) {
    if (!target) {
        return null;
    }

    if (target.jquery) {
        return target.length ? target[0] : null;
    }

    if (typeof target === "string") {
        return document.querySelector(target);
    }

    return target.nodeType === 1 ? target : null;
}

/**
 * Generates a document-unique id so we can point `aria-labelledby` at a heading that has no id.
 *
 * @param {string} prefix - readable prefix for the generated id
 * @returns {string} an id not currently present in the document
 */
function generateId(prefix) {
    var id;

    do {
        idCounter += 1;
        id = prefix + "-" + idCounter;
    } while (document.getElementById(id));

    return id;
}

/**
 * Whether an element is actually being displayed.
 *
 * @param {Element} element - element to test
 * @returns {boolean} true when the element occupies space in the page
 */
function isRendered(element) {
    // offsetParent is null for display:none elements (and for position:fixed, hence the rect check).
    return element.offsetParent !== null || element.getClientRects().length > 0;
}

/**
 * Collects the focusable descendants of a container, in DOM order.
 *
 * Elements that are disabled, removed from the tab order, or not rendered are excluded, so the
 * first/last elements used by the tab trap are always ones the user can actually reach.
 *
 * @param {Element} container - element to search within
 * @returns {Element[]} focusable elements in DOM order
 */
function getFocusableElements(container) {
    var candidates = Array.prototype.slice.call(container.querySelectorAll(FOCUSABLE_SELECTOR));

    return candidates.filter(function (element) {
        if (element.disabled || element.getAttribute("tabindex") === "-1") {
            return false;
        }

        if (element.getAttribute("aria-hidden") === "true") {
            return false;
        }

        return isRendered(element);
    });
}

/**
 * Applies `aria-labelledby` to the dialog so screen readers announce it by name.
 *
 * Uses the caller's id when given, otherwise the first heading inside the dialog - assigning that
 * heading an id if it does not already have one.
 *
 * @param {Element} dialog - the dialog element
 * @param {string} [labelledBy] - id of the element that labels the dialog
 */
function applyLabel(dialog, labelledBy) {
    if (dialog.getAttribute("aria-label")) {
        return;
    }

    var existing = dialog.getAttribute("aria-labelledby");

    // A dangling aria-labelledby - one pointing at an id that is not in the document - leaves the
    // dialog with no accessible name at all, which is worse than having no attribute. Treat it as
    // absent and re-derive rather than trusting it.
    if (existing && resolvesToElements(existing)) {
        return;
    }

    if (labelledBy && resolvesToElements(labelledBy)) {
        dialog.setAttribute("aria-labelledby", labelledBy);
        return;
    }

    var heading = dialog.querySelector("h1,h2,h3,h4,h5,h6");

    if (heading) {
        if (!heading.id) {
            heading.id = generateId("dialog-title");
        }

        dialog.setAttribute("aria-labelledby", heading.id);
        return;
    }

    if (existing) {
        dialog.removeAttribute("aria-labelledby");
    }
}

/**
 * Works out which element should supply the dialog's accessible name.
 *
 * A dialog's name must be short - screen readers speak it in full every time the dialog is
 * announced, and some announce it more than once. Pointing `aria-labelledby` at a wrapper holding
 * the whole body makes the reader recite several sentences before the user reaches any control.
 *
 * `titleSelector` exists for dialogs whose title is neither a heading nor markup we control - the
 * wishlist confirmations render their body from a content asset, so the title line can only be
 * addressed by class. A title found this way is more precise than whatever the markup declared,
 * so it replaces any existing `aria-labelledby`.
 *
 * @param {Element} dialog - the dialog element
 * @param {Object} settings - the options passed to open()
 * @returns {string|undefined} id of the labelling element, if one was determined
 */
function resolveTitleId(dialog, settings) {
    if (settings.labelledBy || !settings.titleSelector) {
        return settings.labelledBy;
    }

    var title = dialog.querySelector(settings.titleSelector);

    if (!title) {
        return undefined;
    }

    if (!title.id) {
        title.id = generateId("dialog-title");
    }

    dialog.removeAttribute("aria-labelledby");

    return title.id;
}

/**
 * Checks that every id in an IDREF list resolves to an element in the document.
 *
 * @param {string} idList - space separated list of element ids
 * @returns {boolean} true when the list is non-empty and every id resolves
 */
function resolvesToElements(idList) {
    var ids = idList.split(/\s+/).filter(Boolean);

    return ids.length > 0 && ids.every(function (id) {
        return !!document.getElementById(id);
    });
}

/**
 * Hides everything outside the dialog from assistive technology and the tab order.
 *
 * Walks from the dialog up to `<body>` marking the siblings at each level, which leaves the
 * dialog's own ancestor chain reachable while inerting the header, main content and the rest of
 * the footer. Prior attribute state is recorded so unrelated `inert` / `aria-hidden` usage
 * elsewhere on the page survives the dialog closing.
 *
 * @param {Element} dialog - the dialog element
 * @returns {Object[]} records describing each element changed, for {@link restoreBackground}
 */
function inertBackground(dialog) {
    var records = [];
    var node = dialog;

    while (node && node !== document.body && node.parentNode) {
        Array.prototype.forEach.call(node.parentNode.children, function (sibling) { // eslint-disable-line no-loop-func
            if (sibling === node || sibling.nodeType !== 1) {
                return;
            }

            if (sibling.tagName === "SCRIPT" || sibling.tagName === "STYLE") {
                return;
            }

            if (sibling.matches(OVERLAY_SELECTOR)) {
                return;
            }

            records.push({
                element: sibling,
                hadInert: sibling.inert === true,
                previousAriaHidden: sibling.getAttribute("aria-hidden")
            });

            if (supportsInert) {
                sibling.inert = true;
            }

            // Set aria-hidden even when inert is supported: inert is the behavioural fix, aria-hidden
            // is what older screen reader / browser pairings actually honour.
            sibling.setAttribute("aria-hidden", "true");
        });

        node = node.parentNode;
    }

    return records;
}

/**
 * Reverts the changes made by {@link inertBackground}.
 *
 * @param {Object[]} records - records returned by {@link inertBackground}
 */
function restoreBackground(records) {
    records.forEach(function (record) {
        if (supportsInert && !record.hadInert) {
            record.element.inert = false;
        }

        if (record.previousAriaHidden === null) {
            record.element.removeAttribute("aria-hidden");
        } else {
            record.element.setAttribute("aria-hidden", record.previousAriaHidden);
        }
    });
}

/**
 * Works out where focus should go when the dialog closes.
 *
 * Prefers the element that was focused when the dialog opened - the triggering control. Auto-launched
 * dialogs have no trigger, so focus was on `<body>`; those fall back to the main landmark.
 *
 * Candidates must still be rendered. A trigger that has been hidden while the dialog was open - a
 * control inside a panel that the dialog's own journey closed, say - cannot take focus, and focusing
 * it silently strands the user on `<body>` instead of falling through to a target that works.
 *
 * @param {Element} [previouslyFocused] - element focused at the point the dialog opened
 * @param {jQuery|Element|string} [returnFocusTo] - explicit override supplied by the caller
 * @returns {Element|null} the element to focus on close
 */
function resolveReturnTarget(previouslyFocused, returnFocusTo) {
    var override = toElement(returnFocusTo);

    if (override && document.body.contains(override) && isRendered(override)) {
        return override;
    }

    if (previouslyFocused
        && previouslyFocused !== document.body
        && document.body.contains(previouslyFocused)
        && isRendered(previouslyFocused)) {
        return previouslyFocused;
    }

    return document.querySelector(MAIN_LANDMARK_SELECTOR);
}

/**
 * Moves focus to an element, making it programmatically focusable first if it is not already.
 *
 * Scrolling is suppressed so returning focus to the main landmark does not throw the user back to
 * the top of a page they had scrolled down. Browsers without `preventScroll` support ignore it.
 *
 * @param {Element} element - element to focus
 */
function focusElement(element) {
    if (!element) {
        return;
    }

    if (!element.hasAttribute("tabindex") && !element.matches(FOCUSABLE_SELECTOR)) {
        element.setAttribute("tabindex", "-1");
    }

    element.focus({ preventScroll: true });
}

/**
 * Finds the open-dialog entry for an element.
 *
 * @param {Element} dialog - the dialog element
 * @returns {Object|null} the tracked entry, or null when the dialog is not open
 */
function findEntry(dialog) {
    for (var i = 0; i < openDialogs.length; i++) {
        if (openDialogs[i].dialog === dialog) {
            return openDialogs[i];
        }
    }

    return null;
}

/**
 * Keeps Tab and Shift+Tab inside the dialog, and closes it on Escape.
 *
 * The tab trap is needed alongside `inert` because `inert` is unsupported on older browsers and,
 * where it is supported, does not stop focus cycling out to the browser chrome and back in.
 *
 * @param {Object} entry - the tracked dialog entry
 * @returns {Function} the keydown handler, so it can be unbound on close
 */
function createKeydownHandler(entry) {
    return function (event) {
        var key = event.which || event.keyCode;

        // Only the topmost dialog responds, so Escape closes one layer at a time.
        if (openDialogs[openDialogs.length - 1] !== entry) {
            return;
        }

        // A nested Bootstrap modal - the size guide inside the quick view drawer, say - handles its
        // own Escape and traps its own focus, so the dialog around it stands aside until it closes.
        // Without this, one Escape closes both layers (DGF-3991).
        if (entry.dialog.querySelector(".modal.show")) {
            return;
        }

        if (key === ESCAPE_KEY && entry.options.dismissible !== false) {
            event.preventDefault();
            module.exports.close(entry.dialog);
            return;
        }

        if (key !== TAB_KEY) {
            return;
        }

        var focusable = getFocusableElements(entry.dialog);

        if (!focusable.length) {
            // Nothing to tab to - hold focus on the dialog itself rather than letting it escape.
            event.preventDefault();
            focusElement(entry.dialog);
            return;
        }

        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        var active = document.activeElement;

        if (event.shiftKey && (active === first || !entry.dialog.contains(active))) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && (active === last || !entry.dialog.contains(active))) {
            event.preventDefault();
            first.focus();
        }
    };
}

module.exports = {
    /**
     * Opens a dialog accessibly.
     *
     * Sets the dialog roles, moves focus into the dialog, inerts the rest of the page and binds the
     * Escape / tab-trap key handling. Making the dialog visible remains the caller's job - this
     * helper does not assume how the dialog is shown (Bootstrap plugin, class toggle or inline style).
     *
     * @param {jQuery|Element|string} target - the dialog element
     * @param {Object} [options] - dialog options
     * @param {string} [options.labelledBy] - id of the dialog's labelling element; defaults to its first heading
     * @param {Function} [options.show] - makes the dialog visible. Pass it rather than showing the dialog yourself: the helper applies the roles, name and background inerting first, so the only thing that changes once the dialog is on screen is focus
     * @param {string} [options.titleSelector] - selector, within the dialog, for the short title line to name it by. Use when the title is not a heading and not in markup you control; it overrides any `aria-labelledby` already on the dialog
     * @param {jQuery|Element|string} [options.initialFocus] - element (or selector within the dialog) to focus on open
     * @param {jQuery|Element|string} [options.returnFocusTo] - where focus goes on close; defaults to the trigger, then `#maincontent`
     * @param {boolean} [options.dismissible=true] - whether Escape closes the dialog
     * @param {Function} [options.onClose] - called after the background is restored and before focus returns; hide or remove the dialog here
     * @returns {boolean} true when the dialog was opened, false when it could not be resolved or was already open
     */
    open: function (target, options) {
        var dialog = toElement(target);

        if (!dialog || findEntry(dialog)) {
            return false;
        }

        var settings = options || {};

        var entry = {
            dialog: dialog,
            options: settings,
            previouslyFocused: document.activeElement,
            inertRecords: []
        };

        dialog.setAttribute("role", "dialog");
        dialog.setAttribute("aria-modal", "true");
        applyLabel(dialog, resolveTitleId(dialog, settings));

        entry.inertRecords = inertBackground(dialog);
        openDialogs.push(entry);

        entry.keydownHandler = createKeydownHandler(entry);
        document.addEventListener("keydown", entry.keydownHandler, true);

        // Reveal the dialog only now that it is fully described and the background is already
        // hidden. Changing roles, names or the accessibility tree while the dialog is on screen
        // gives screen readers a second thing to react to, and they announce it twice (DGF-3989).
        if (typeof settings.show === "function") {
            settings.show(dialog);
        }

        var initialFocus = null;

        if (typeof settings.initialFocus === "string") {
            initialFocus = dialog.querySelector(settings.initialFocus);
        } else if (settings.initialFocus) {
            initialFocus = toElement(settings.initialFocus);
        }

        focusElement(initialFocus || getFocusableElements(dialog)[0] || dialog);

        return true;
    },

    /**
     * Closes a dialog accessibly.
     *
     * Un-inerts the page, hands control back to the caller via `onClose` so it can hide or remove the
     * dialog, then returns focus. Focus is restored last so it still lands correctly for dialogs that
     * remove themselves from the DOM.
     *
     * @param {jQuery|Element|string} target - the dialog element
     * @returns {boolean} true when the dialog was open and has been closed
     */
    close: function (target) {
        var dialog = toElement(target);
        var entry = dialog ? findEntry(dialog) : null;

        if (!entry) {
            return false;
        }

        openDialogs.splice(openDialogs.indexOf(entry), 1);
        document.removeEventListener("keydown", entry.keydownHandler, true);

        restoreBackground(entry.inertRecords);
        dialog.removeAttribute("aria-modal");

        var returnTarget = resolveReturnTarget(entry.previouslyFocused, entry.options.returnFocusTo);

        if (typeof entry.options.onClose === "function") {
            entry.options.onClose(dialog);
        }

        focusElement(returnTarget);

        return true;
    },

    /**
     * Whether a dialog is currently open and tracked by this helper.
     *
     * @param {jQuery|Element|string} target - the dialog element
     * @returns {boolean} true when the dialog is open
     */
    isOpen: function (target) {
        var dialog = toElement(target);

        return !!(dialog && findEntry(dialog));
    },

    /**
     * Adds the missing accessibility layer to every Bootstrap-plugin modal on the page.
     *
     * Bootstrap 4 already traps focus, so this deliberately does not - it only supplies what the
     * plugin lacks: `aria-modal`, a working `aria-labelledby`, an inert background, and focus
     * return. Bootstrap only restores focus for modals opened through its `data-toggle="modal"`
     * data-api (modal.js registers the restorer in that click handler), so every modal opened
     * programmatically with `.modal("show")` - which is all of the auto-launched ones - would
     * otherwise strand focus at the top of the document on close.
     *
     * Modals already being managed by {@link open} are skipped, so the two paths cannot fight.
     *
     * Safe to call more than once; only the first call binds.
     *
     * @returns {boolean} true when the handlers were bound by this call
     */
    enhanceBootstrapModals: function () {
        if (bootstrapEnhanced) {
            return false;
        }

        bootstrapEnhanced = true;

        $(document).on("show.bs.modal", ".modal", function () {
            if (findEntry(this)) {
                return;
            }

            this.setAttribute("aria-modal", "true");
            applyLabel(this);

            // Recorded before Bootstrap moves focus into the modal, so this is still the trigger.
            bootstrapRecords.set(this, {
                previouslyFocused: document.activeElement,
                inertRecords: []
            });
        });

        $(document).on("shown.bs.modal", ".modal", function () {
            var record = bootstrapRecords.get(this);

            if (record) {
                record.inertRecords = inertBackground(this);
            }
        });

        $(document).on("hidden.bs.modal", ".modal", function () {
            var record = bootstrapRecords.get(this);

            if (!record) {
                return;
            }

            bootstrapRecords.delete(this);
            restoreBackground(record.inertRecords);
            this.removeAttribute("aria-modal");

            focusElement(resolveReturnTarget(record.previouslyFocused));
        });

        return true;
    }
};
